/**
 * Range and day-bucket helpers shared by the history routes (rank, estimates,
 * ratings). Every history table on our side carries more than one row per
 * day for one app: `app_rankings` writes the overall chart and the category
 * chart on the same `scraped_date`, and `app_revenue` writes one row per
 * fetch country. A chart wants one point per calendar day, and the newest
 * row of that day is the one to keep.
 */

export const RANGES = ['30d', '90d', '1y', 'all'] as const;
export type HistoryRange = (typeof RANGES)[number];

const RANGE_DAYS: Record<Exclude<HistoryRange, 'all'>, number> = { '30d': 30, '90d': 90, '1y': 365 };

export function isHistoryRange(value: unknown): value is HistoryRange {
  return typeof value === 'string' && (RANGES as readonly string[]).includes(value);
}

/** The `?range=` query value, or the fallback when it is absent or not one of ours. */
export function parseRange(value: string | null | undefined, fallback: HistoryRange): HistoryRange {
  return isHistoryRange(value) ? value : fallback;
}

/** `YYYY-MM-DD` of the first day inside the range, or null for `all` (no lower bound). */
export function rangeSince(range: HistoryRange, now: Date = new Date()): string | null {
  if (range === 'all') return null;
  const since = new Date(now);
  since.setUTCDate(since.getUTCDate() - RANGE_DAYS[range]);
  return isoDay(since.toISOString());
}

/**
 * PostgREST answers at most 1000 rows per request whatever `limit` asks for,
 * so a history longer than that is read page by page. `page(from, to)` builds
 * one request for that row window; `cap` bounds the total.
 */
export async function allPages<T>(
  page: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: { message: string } | null }>,
  cap = 5000,
): Promise<T[]> {
  const PAGE = 1000;
  const rows: T[] = [];
  while (rows.length < cap) {
    const take = Math.min(PAGE, cap - rows.length);
    const { data, error } = await page(rows.length, rows.length + take - 1);
    if (error) throw new Error(error.message);
    const batch = data ?? [];
    rows.push(...batch);
    // A short page means the result set is exhausted (take never exceeds PAGE).
    if (batch.length < take) break;
  }
  return rows;
}

/**
 * A number out of a PostgREST `->>` selection, which always answers text, or null when the
 * value is absent or does not parse. Both `app_st` readers pull their numbers out of
 * `st_raw` this way, so a row that cannot be parsed is dropped rather than charted as NaN.
 */
export function parseNumeric(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/** `YYYY-MM-DD` out of an ISO timestamp or date string. */
export function isoDay(value: string): string {
  return value.slice(0, 10);
}

/**
 * One row per calendar day, ascending by day, keeping the row with the newest
 * `at` timestamp inside each day. Input order does not matter.
 */
export function latestPerDay<T>(rows: readonly T[], dayOf: (row: T) => string, atOf: (row: T) => string): T[] {
  const byDay = new Map<string, T>();
  for (const row of rows) {
    const day = dayOf(row);
    const held = byDay.get(day);
    if (!held || atOf(row) > atOf(held)) byDay.set(day, row);
  }
  return [...byDay.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([, row]) => row);
}
